import { httpsCallable } from 'firebase/functions'
import { getFns } from '../../firebase'
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '../../lib/ToastContext'
import ConfirmModal from '../../components/ui/ConfirmModal'
import Page from '../../components/layout/Page'
import Icon from '../../components/AppIcon'
import ProductForm from './components/ProductForm'
import PendingProducts from './components/PendingProducts'
import PendingProductEditPanel from './components/PendingProductEditPanel'
import UpdateTab from './components/UpdateTab'
import CodeGeneratorTab from './components/CodeGeneratorTab'
import GoogleFixesTab from './components/GoogleFixesTab'
import TagConfigSidebar from './components/TagConfigSidebar'
import PostPublishTagPreviewModal from './components/PostPublishTagPreviewModal'
import { useProducts } from './hooks/useProducts'
import { useShopifyProducts } from './hooks/useShopifyProducts'
import { useAIProcessing } from './hooks/useAIProcessing'
import { useTagConfig } from './hooks/useTagConfig'
import { usePendingProducts } from './hooks/usePendingProducts'
import { printTags } from './utils/printTag'
import { logEvent } from '../../lib/blackbox'
import OfficeWorkspaceShell from '../../components/layout/OfficeWorkspaceShell'
import { PORTAL_MAIN_MENU } from '../../lib/portalNav'

const TABS = [
  { id: 'listing', label: 'Listing', icon: 'Plus' },
  { id: 'codes', label: 'Codes', icon: 'Barcode' },
  { id: 'google-fixes', label: 'Google Fixes', icon: 'Search' },
  { id: 'pending', label: 'Pending', icon: 'Clock' },
  { id: 'update', label: 'Update', icon: 'RefreshCw' },
  { id: 'settings', label: 'Settings', icon: 'Settings' }
]

const TAG_PRINT_INFO_MAX = 24
const SETTINGS_PREVIEW_PRODUCT = {
  id: 'settings-preview',
  title: 'Sample Sovereign Ring',
  price: '299.99',
  sku: '17010001',
  barcode: '17010009',
  weight: '3.5',
  karat: '14',
  productType: 'Ring 14K Gold',
  tagPrintInfo: 'AU14K RING CZ1.2CT 3.5G',
  metafields: {
    karat: '14',
    tag_print_info: 'AU14K RING CZ1.2CT 3.5G'
  }
}

function mapProductToTagPayload(product) {
  if (!product) return null
  const variant = product?.variants?.[0] || {}
  const tagPrintInfo = String(product?.tagPrintInfo || product?.metafields?.tag_print_info || '').trim().slice(0, TAG_PRINT_INFO_MAX)

  return {
    id: String(product?.id || product?.shopifyGid || product?.sku || ''),
    title: product?.title || '',
    price: variant?.price || product?.salePrice || product?.price || '0',
    sku: variant?.sku || product?.sku || '',
    barcode: variant?.barcode || product?.barcode || '',
    weight: variant?.weight || product?.weight || product?.metafields?.weight || '',
    karat: product?.metafields?.karat || '',
    productType: product?.productType || '',
    tagPrintInfo,
    metafields: {
      ...(product?.metafields || {}),
      tag_print_info: tagPrintInfo
    }
  }
}

export default function ProductListingPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const activeTab = TABS.some(tab => tab.id === requestedTab) ? requestedTab : 'listing'
  const [isCompactViewport, setIsCompactViewport] = useState(false)

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 900 || window.innerHeight <= 850)
    }
    updateViewportMode()
    window.addEventListener('resize', updateViewportMode)
    return () => window.removeEventListener('resize', updateViewportMode)
  }, [])

  const setActiveTab = useCallback((tab) => {
    setSearchParams({ tab })
  }, [setSearchParams])

  const {
    createProduct,
    updateProduct,
    deleteProduct: deleteLocalProduct
  } = useProducts()

  const {
    products: shopifyProducts,
    loading: shopifyLoading,
    error: shopifyError,
    lastSync: shopifyLastSync,
    updatePrice: updateShopifyPrice,
    ensureBarcode: ensureShopifyBarcode,
    updateInventory: updateShopifyInventory,
    updateProductFields: shopifyUpdateProductFields,
    updateImage: updateShopifyImage,
    deleteFromShopify,
    searchProducts: searchShopifyProducts,
    loadMore: loadMoreShopify,
    hasMore: shopifyHasMore,
    syncMore: syncShopifyMore,
    syncing: shopifySyncing
  } = useShopifyProducts()

  const {
    processProduct,
    generateUpdateSuggestions,
    generateGoogleMetadataFix
  } = useAIProcessing()
  const {
    tagConfig,
    updateTagConfig,
    saveTagConfig,
    saving: savingTagConfig
  } = useTagConfig()
  const { pendingProducts, loading: pendingLoading, markActive } = usePendingProducts()

  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [settingsTagPrintInfo, setSettingsTagPrintInfo] = useState(SETTINGS_PREVIEW_PRODUCT.tagPrintInfo)
  const [postPublishTagProduct, setPostPublishTagProduct] = useState(null)
  const [selectedPendingId, setSelectedPendingId] = useState(null)

  const settingsPreviewProduct = useMemo(() => {
    const tagPrintInfo = String(settingsTagPrintInfo || '').trim().slice(0, TAG_PRINT_INFO_MAX)
    return {
      ...SETTINGS_PREVIEW_PRODUCT,
      tagPrintInfo,
      metafields: {
        ...(SETTINGS_PREVIEW_PRODUCT.metafields || {}),
        tag_print_info: tagPrintInfo
      }
    }
  }, [settingsTagPrintInfo])

  const handleRetryPublish = async (productId) => {
    try {
      await httpsCallable(getFns(), 'productListingRetryPublish', { timeout: 540000 })({ productId })
      toast.success('Product uploaded to Shopify')
    } catch (error) {
      toast.error(error.message || 'Could not upload to Shopify. Review the product error and retry.')
    }
  }

  const handleRetryInventory = async (productId, quantity) => {
    const result = await httpsCallable(getFns(), 'productListingRetryPublish', { timeout: 120000 })({ action: 'inventory', productId, quantity })
    if (result.data?.ok !== true) throw new Error('Shopify did not confirm the stock.')
    toast.success('Shopify stock confirmed')
  }

  const handleSave = async (productData, imageFile) => {
    setSaving(true)
    try {
      const createdId = await createProduct(productData, imageFile)
      setPostPublishTagProduct(mapProductToTagPayload({
        id: createdId,
        ...productData
      }))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (productId) => setDeleteTarget(productId)
  const confirmDelete = async () => {
    const productId = deleteTarget
    setDeleteTarget(null)
    try {
      const pendingProduct = pendingProducts.find((item) => item.id === productId) || null
      logEvent({
        action: 'delete_product',
        level: 'warn',
        severity: 'red',
        message: 'Delete product requested from pending tab',
        data: {
          productId,
          shopifyId: pendingProduct?.shopifyId || null,
          shopifyGid: pendingProduct?.shopifyGid || null
        }
      })
      const shopifyRef = pendingProduct?.shopifyGid || pendingProduct?.shopifyId

      if (shopifyRef) {
        try {
          await deleteFromShopify({
            productId: pendingProduct?.shopifyId || shopifyRef,
            shopifyId: pendingProduct?.shopifyId || null,
            shopifyGid: pendingProduct?.shopifyGid || null,
            cacheDocId: pendingProduct?.shopifyId || null
          })
        } catch (shopifyErr) {
          const msg = String(shopifyErr?.message || '')
          const code = String(shopifyErr?.code || '')
          const alreadyMissing = /not-found/i.test(code) || /not found|does not exist/i.test(msg)
          if (!alreadyMissing) {
            throw shopifyErr
          }
        }
      }

      await deleteLocalProduct(productId)
      logEvent({
        action: 'delete_product',
        level: 'warn',
        severity: 'red',
        message: 'Delete product completed',
        data: {
          productId,
          deletedInShopify: !!shopifyRef
        }
      })
      if (selectedPendingId === productId) {
        setSelectedPendingId(null)
      }
    } catch (err) {
      logEvent({
        action: 'delete_product',
        level: 'error',
        severity: 'red',
        message: err?.message || 'Delete product failed',
        data: {
          productId
        }
      })
      const message = err?.message || 'Failed to delete product'
      toast.error(message)
    }
  }

  const handlePrintTag = useCallback((product) => {
    const tagProduct = mapProductToTagPayload(product)
    if (!tagProduct) return
    printTags([tagProduct], tagConfig)
  }, [tagConfig])

  const handlePrintSettingsPreview = useCallback(() => {
    printTags([settingsPreviewProduct], tagConfig)
  }, [settingsPreviewProduct, tagConfig])

  const handleSaveLabelSettings = useCallback(async () => {
    try {
      await saveTagConfig()
      toast.success('Label settings saved')
    } catch (err) {
      console.error('Failed to save label settings:', err)
      toast.error('Failed to save label settings')
    }
  }, [saveTagConfig, toast])

  const handleRegenerateFields = useCallback(async (imageUrl, currentData) => {
    if (!imageUrl) return null
    const result = await processProduct(imageUrl, currentData.title || '', {
      costPrice: 0,
      salePrice: parseFloat(currentData.price) || 0
    })
    return result
  }, [processProduct])

  const updateProductFields = useCallback(async (productId, fields) => {
    return shopifyUpdateProductFields(productId, fields)
  }, [shopifyUpdateProductFields])

  const selectedPendingProduct = useMemo(
    () => pendingProducts.find((item) => item.id === selectedPendingId) || null,
    [pendingProducts, selectedPendingId]
  )

  useEffect(() => {
    if (!selectedPendingId) return
    if (!pendingProducts.some((item) => item.id === selectedPendingId)) {
      setSelectedPendingId(null)
    }
  }, [pendingProducts, selectedPendingId])

  const handlePendingSave = useCallback(async (productId, fields) => {
    await updateProduct(productId, fields)
  }, [updateProduct])

  const handlePendingMarkActive = useCallback(async (productId) => {
    await markActive(productId)
    if (selectedPendingId === productId) {
      setSelectedPendingId(null)
    }
  }, [markActive, selectedPendingId])

  const operations = useMemo(
    () => PORTAL_MAIN_MENU.map(item => ({
      ...item,
      onClick: () => navigate(item.path)
    })),
    [navigate]
  )

  const pendingCount = pendingProducts.length
  const isLabelSettingsTab = activeTab === 'settings'
  const pageTitle = isLabelSettingsTab ? 'Settings' : 'Product Listing'
  const pageSubtitle = isLabelSettingsTab
    ? 'Configure print settings and future modules'
    : 'Create and manage jewelry and perfume listings'
  const sectionLabel = isLabelSettingsTab ? 'Settings' : 'Product Listing'
  const sectionDescription = isLabelSettingsTab
    ? 'Adjust settings for labels and other future modules'
    : 'Create and manage jewelry and perfume listings'

  return (
    <Page
      size="full"
      title={pageTitle}
      subtitle={pageSubtitle}
    >
      <OfficeWorkspaceShell
        activeSectionId="product_listing"
        activeSectionLabel={sectionLabel}
        activeSectionIcon="Tag"
        activeSectionDescription={sectionDescription}
        operations={[]}
        workspaces={[]}
        operationsTitle="Menu"
        compactMenu
        layoutPreset="office"
        rightPanel={activeTab === 'settings' ? (
          <TagConfigSidebar
            tagConfig={tagConfig}
            onUpdateConfig={updateTagConfig}
            selectedProduct={settingsPreviewProduct}
            tagPrintInfo={settingsTagPrintInfo}
            onTagPrintInfoChange={setSettingsTagPrintInfo}
            tagPrintInfoMax={TAG_PRINT_INFO_MAX}
            onOpenPrintPreview={handlePrintSettingsPreview}
            onSaveSettings={handleSaveLabelSettings}
            saveBusy={savingTagConfig}
          />
        ) : null}
      >
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-4 border-b border-border/60 pb-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap font-medium transition-colors border-b-2 -mb-px ${
                isCompactViewport ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
              } ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border/60'
              }`}
            >
              <Icon name={tab.icon} size={isCompactViewport ? 14 : 16} />
              {tab.label}
              {tab.id === 'pending' && pendingCount > 0 && (
                <span className="ml-1 bg-primary/10 text-primary text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'listing' && (
          <div className={isCompactViewport ? 'block' : 'grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(260px,1fr)] gap-4'}>
            <ProductForm
              onSave={handleSave}
              saving={saving}
              editProduct={editingProduct}
            />

            {!isCompactViewport && (
              <aside className="bg-card rounded-xl border border-border/60 shadow-elevation-1 p-4 h-fit xl:sticky xl:top-20">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Lightbulb" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Tips & Recommendations</h3>
                </div>

                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>Use clear product titles with material and type first.</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>Confirm SKU and barcode before saving to prevent print issues.</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>Add focused photos with neutral background for AI quality.</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>Fill product type and tags to improve search and filtering.</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CircleDot" size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>Double-check pricing and weight before final save.</span>
                  </li>
                </ul>
              </aside>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div className={isCompactViewport ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 xl:grid-cols-[minmax(320px,1.1fr)_minmax(420px,1fr)] gap-4'}>
            <PendingProducts
              products={pendingProducts}
              loading={pendingLoading}
              onMarkActive={handlePendingMarkActive}
              onRetryPublish={handleRetryPublish}
              onRetryInventory={handleRetryInventory}
              onPrintTag={handlePrintTag}
              selectedId={selectedPendingId}
              onSelect={(product) => setSelectedPendingId(product?.id || null)}
            />

            {selectedPendingProduct ? (
              <PendingProductEditPanel
                product={selectedPendingProduct}
                onSave={handlePendingSave}
                onDelete={handleDelete}
                onClose={() => setSelectedPendingId(null)}
              />
            ) : (
              <div className="bg-card rounded-xl border border-border/60 shadow-elevation-1 flex flex-col items-center justify-center p-8 min-h-[400px]">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground/40 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Select a pending product to edit</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  SKU and barcode stay locked, other fields are editable.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'codes' && (
          <CodeGeneratorTab compactViewport={isCompactViewport} />
        )}

        {activeTab === 'google-fixes' && (
          <GoogleFixesTab
            generateGoogleMetadataFix={generateGoogleMetadataFix}
            updateProductFields={updateProductFields}
            compactViewport={isCompactViewport}
          />
        )}

        {activeTab === 'update' && (
          <UpdateTab
            shopifyProducts={shopifyProducts}
            shopifyLoading={shopifyLoading}
            shopifyError={shopifyError}
            shopifyLastSync={shopifyLastSync}
            searchShopifyProducts={searchShopifyProducts}
            loadMoreShopify={loadMoreShopify}
            shopifyHasMore={shopifyHasMore}
            syncShopifyMore={syncShopifyMore}
            shopifySyncing={shopifySyncing}
            updateShopifyPrice={updateShopifyPrice}
            ensureShopifyBarcode={ensureShopifyBarcode}
            updateShopifyInventory={updateShopifyInventory}
            updateProductFields={updateProductFields}
            updateShopifyImage={updateShopifyImage}
            regenerateFields={handleRegenerateFields}
            generateUpdateSuggestions={generateUpdateSuggestions}
            tagConfig={tagConfig}
            onPrintTag={handlePrintTag}
            compactViewport={isCompactViewport}
          />
        )}

        {activeTab === 'settings' && (
          <div className="bg-card rounded-2xl border border-border/60 p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Icon name="Settings" size={18} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Settings</h3>
                <p className="text-sm text-muted-foreground">
                  Use the third column to configure Label Setting. More setting modules will be added here later.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
              <p className="text-sm text-foreground font-medium mb-1">Label Setting moved to the right panel.</p>
              <p className="text-sm text-muted-foreground">
                Configure fields, fonts, alignment, barcode options, reference preview, and Tag Print Info in the third column.
              </p>
            </div>
          </div>
        )}
      </OfficeWorkspaceShell>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        icon="Trash2"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <PostPublishTagPreviewModal
        open={!!postPublishTagProduct}
        product={postPublishTagProduct}
        tagConfig={tagConfig}
        onClose={() => setPostPublishTagProduct(null)}
      />
    </Page>
  )
}
